import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/meetings.css';
import AppSidebar from '../components/layout/AppSidebar';
import SpinningLoader from '../components/ui/SpinningLoader';
import Toast from '../components/ui/Toast';
import MeetingsListView from '../components/views/meetings/MeetingsListView';
import MeetingWorkspaceView from '../components/views/meetings/MeetingWorkspaceView';
import CreateMeetingModal from '../components/modals/CreateMeetingModal';
import Modal from '../components/ui/Modal';
import { Meeting, CreateMeetingRequest } from '../types/meeting';
import meetingsAPI from '../services/meetingsApi';
import { useAuth } from '../contexts/AuthContext';

const Meetings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { isSecretary } = useAuth();

  // Live view (/meetings/live): the Meetings list filtered to not-yet-completed
  // meetings (draft/prepared), reusing the same page + data.
  const liveOnly = location.pathname === '/meetings/live';

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | undefined>();
  const [loading, setLoading] = useState(true);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Meeting | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('error');

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  // `silent` refreshes the list in the background without swapping the page for
  // the full-page loader — used to reconcile after an optimistic update.
  const loadMeetings = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const list = await meetingsAPI.listMeetings({ limit: 100 });
      setMeetings(list);
    } catch (err) {
      console.error('Error loading meetings:', err);
      if (!silent) showToast('Something went wrong. Failed to fetch meetings', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  useEffect(() => {
    if (id) {
      const loadMeeting = async () => {
        setMeetingLoading(true);
        setSelectedMeeting(undefined);
        try {
          const meeting = await meetingsAPI.getMeeting(id);
          setSelectedMeeting(meeting);
        } catch (err) {
          console.error('Error loading meeting:', err);
          showToast('Failed to load meeting', 'error');
          setTimeout(() => navigate('/meetings'), 1500);
        } finally {
          setMeetingLoading(false);
        }
      };
      loadMeeting();
    } else {
      setSelectedMeeting(undefined);
    }
  }, [id, navigate]);

  const handleCreateMeeting = async (request: CreateMeetingRequest) => {
    try {
      const meeting = await meetingsAPI.createMeeting(request);
      setCreateModalOpen(false);
      showToast(`"${meeting.title}" has been created`, 'success');
      await loadMeetings();
      navigate(`/meetings/${meeting.meeting_id}`);
    } catch (err) {
      console.error('Error creating meeting:', err);
      showToast('Something went wrong. Failed to create meeting', 'error');
      throw err;
    }
  };

  // Deleting server-side can be slow, so the UI settles immediately: close the
  // dialog, drop the meeting from the list, and reconcile with the server after.
  // A failed delete puts the meeting back and reports the error.
  const handleDeleteMeeting = () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    const previousMeetings = meetings;

    setDeleteTarget(null);
    setMeetings((prev) => prev.filter((m) => m.meeting_id !== target.meeting_id));
    showToast(`"${target.title}" has been deleted`, 'success');
    if (id === target.meeting_id) {
      navigate('/meetings');
    }

    void (async () => {
      try {
        await meetingsAPI.deleteMeeting(target.meeting_id);
        await loadMeetings(true);
      } catch (err) {
        console.error('Error deleting meeting:', err);
        setMeetings(previousMeetings);
        showToast(`Failed to delete "${target.title}"`, 'error');
      }
    })();
  };

  const renderContent = () => {
    if (id) {
      if (meetingLoading || !selectedMeeting) {
        return (
          <div className="dashboard-content">
            <div className="dashboard-loading">
              <SpinningLoader size="large" text="Loading meeting..." />
            </div>
          </div>
        );
      }
      return (
        <MeetingWorkspaceView
          meeting={selectedMeeting}
          onBack={() => navigate('/meetings')}
          onMeetingUpdated={(m) => setSelectedMeeting(m)}
          onError={(msg) => showToast(msg, 'error')}
        />
      );
    }

    if (loading) {
      return (
        <div className="dashboard-content">
          <div className="dashboard-loading">
            <SpinningLoader size="large" text="Loading meetings..." />
          </div>
        </div>
      );
    }

    return (
      <MeetingsListView
        meetings={meetings}
        liveOnly={liveOnly}
        canManage={isSecretary}
        onCreateMeeting={() => setCreateModalOpen(true)}
        onViewMeeting={(m) => navigate(`/meetings/${m.meeting_id}`)}
        onDeleteMeeting={(m) => setDeleteTarget(m)}
      />
    );
  };

  return (
    <div className="aspire-dashboard">
      <AppSidebar />

      {renderContent()}

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        duration={4000}
      />

      <CreateMeetingModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateMeeting}
      />

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete meeting?"
        maxWidth="460px"
      >
        <div>
          <p style={{ fontSize: 14, color: '#5b6472', margin: '4px 0 20px 0' }}>
            Deleting "{deleteTarget?.title}" will permanently remove the meeting, its agenda,
            transcript, and minutes. This cannot be undone.
          </p>
          <div className="meeting-form-actions">
            <button className="meeting-btn meeting-btn-secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </button>
            <button
              className="meeting-btn meeting-btn-primary"
              style={{ background: '#b42318' }}
              onClick={handleDeleteMeeting}
            >
              Delete Meeting
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Meetings;
